<template>
    <div class="organization-dashboard-tab">
        <GuideVideoBanner 
            v-if="guideData && guideData.content && showGuideCard"
            :text="guideData.content.text"
            :subtext="guideData.content.subText"
            :videoUrl="guideData.content.videoUrl"
            :videoThumbnail="guideData.content.videoThumbnail"
            @close="handleGuideClose"
        />
        <OrganizationDashboardSection />
        <IdLikeToOrganizationSection 
            :has_positions="has_positions"
            :has_roles="has_roles"
            :has_responsibilities="has_responsibilities"
            :has_employees="has_employees"
            :learn_about_positions_url="learn_about_positions_url"
            :learn_about_roles_url="learn_about_roles_url"
            :learn_about_responsibilities_url="learn_about_responsibilities_url"
            :learn_about_employees_url="learn_about_employees_url"
            @change-tab="handleTabChange"
            @add-position="showAddPositionModal = true"
            @add-role="showAddRoleModal = true"
            @add-responsibility="showAddResponsibilityModal = true"
            @add-employee="showAddEmployeeModal = true"
        />

        <!-- Add Position Modal -->
        <AddOrgObject
            :show="showAddPositionModal"
            OrgObjectType="Position"
            ghostText="+ Add"
            description="You are about to add a new"
            type="add"
            :button-label="isLoading ? 'Saving' : 'Save'"
            @onAdd="handleAddPosition"
            @close="showAddPositionModal = false"
        />

        <!-- Add Role Modal -->
        <AddRoleDialog
            :value="showAddRoleModal"
            @input="showAddRoleModal = $event"
            type="add"
            @onSave="handleAddRole"
            @clearError="clearRoleError"
            :errorMessage="roleErrorMessage"
            :existingNames="[]"
        />

        <!-- Add Responsibility Modal -->
        <AddResponsibility
            :value="showAddResponsibilityModal"
            @input="showAddResponsibilityModal = $event"
            @onSave="handleAddResponsibility($event)"
            @clearError="clearResponsibilityError"
            :errorMessage="responsibilityErrorMessage"
        />

        <!-- Add Employee Modal -->
        <AddEmployeeDialog
            :show="showAddEmployeeModal"
            @onSave="handleAddEmployee"
            @close="showAddEmployeeModal = false"
        />
    </div>
</template>

<script setup>
import { ref, computed, onMounted, getCurrentInstance } from 'vue';
import GuideVideoBanner from '@components/GuideVideoBanner/GuideVideoBanner.vue';
import OrganizationDashboardSection from '@components/organizations/dashboard/OrganizationDashboardSection.vue';
import IdLikeToOrganizationSection from '@components/IdLikeToOrganizationSection/IdLikeToOrganizationSection.vue';
import AddOrgObject from '@components/AddOrgObject/AddOrgObject.vue';
import AddEmployeeDialog from '@components/organizations/employees/AddEmployeeDialog/AddEmployeeDialog.vue';
import AddResponsibility from '@components/RolesCatalog/AddResponsibilityDialog/AddResponsibilityDialog.vue';
import AddRoleDialog from '@components/RolesCatalog/AddRoleDialog/AddRoleDialog.vue';
import Knock, { KnockGuideClient } from "@knocklabs/client";
import configService from "../../account/src/services/config";
import { useSidebarCountStore } from '../../account/src/store/pinia/sidebarCount';
import { createPosition, addRole, createResponsibility, createEmployee } from '@/api';

const app = getCurrentInstance();
const { proxy } = app;

const props = defineProps({
    guideData: {
        type: Object,
        default: null
    },
    has_positions: {
        type: Boolean,
        default: null
    },
    has_roles: {
        type: Boolean,
        default: null
    },
    has_responsibilities: {
        type: Boolean,
        default: null
    },
    has_employees: {
        type: Boolean,
        default: null
    },
    learn_about_positions_url: {
        type: String,
        default: null
    },
    learn_about_roles_url: {
        type: String,
        default: null
    },
    learn_about_responsibilities_url: {
        type: String,
        default: null
    },
    learn_about_employees_url: {
        type: String,
        default: null
    }
});

const sidebarCountStore = useSidebarCountStore();

const has_positions = computed(() => props.has_positions !== null ? props.has_positions : sidebarCountStore.getPositionsCount > 0);
const has_roles = computed(() => props.has_roles !== null ? props.has_roles : sidebarCountStore.getRolesCount > 0);
const has_responsibilities = computed(() => props.has_responsibilities !== null ? props.has_responsibilities : sidebarCountStore.getResponsibilitiesCount > 0);
const has_employees = computed(() => props.has_employees !== null ? props.has_employees : sidebarCountStore.getEmployeesCount > 0);

const emit = defineEmits(['guide-closed', 'change-tab', 'drilldown']);

const fetchedGuideData = ref(null);
const knockClient = ref(null);
const guideClient = ref(null);
const guides = ref([]);
const showGuideCard = ref(true);

// Modal states
const showAddPositionModal = ref(false);
const showAddRoleModal = ref(false);
const showAddResponsibilityModal = ref(false);
const showAddEmployeeModal = ref(false);
const isLoading = ref(false);
const responsibilityErrorMessage = ref('');
const roleErrorMessage = ref('');

// Use prop guideData if provided (for Storybook), otherwise use fetched data
const guideData = computed(() => props.guideData || fetchedGuideData.value);

const initializeKnock = async () => {
    try {
        const authUser = JSON.parse(localStorage.getItem("user"));
        const apiKey = configService.get("knockApiKey");
        const userId = authUser?.id?.toString();
        const channelId = configService.get("knockGuideChannelId");

        knockClient.value = new Knock(apiKey);
        knockClient.value.authenticate({ id: userId });

        guideClient.value = new KnockGuideClient(knockClient.value, channelId);

        guideClient.value.store.subscribe(() => {
            const storeState = guideClient.value.store.state;
            guides.value = guideClient.value.selectGuides(storeState) || [];
            const adminGuide = guides.value.find(
                (guide) => guide.key === "welcome-as-account-admin"
            );
            fetchedGuideData.value = adminGuide?.steps?.[0] || null;
        });

        await guideClient.value.subscribe();
        await guideClient.value.fetch();
    } catch (error) {
        console.error("Error initializing Knock:", error);
    }
};

const handleGuideClose = async () => {
    showGuideCard.value = false;
    
    if (guideData.value && guideData.value.markAsArchived) {
        try {
            await guideData.value.markAsArchived();
        } catch (error) {
            console.error('Error archiving guide in Knock:', error);
        }
    }
    
    emit('guide-closed');
};

const handleTabChange = (tabName) => {
    emit('change-tab', tabName);
};

const handleAddPosition = async (name) => {
    try {
        isLoading.value = true;
        proxy.$loader.setAppLoading(true);
        const accountID = localStorage.getItem('currentAccountID');
        
        let { data } = await createPosition(accountID, { name });

        if (data?.id) {
            showAddPositionModal.value = false;
            proxy.$loader.setAppLoading(false);
            await proxy.$nextTick();
            
            // Navigate to position detail page using the same route as OrganizationPositionsTab
            proxy.$router.push({
                name: 'PositionDetailChild',
                params: { positionId: data.id },
                query: { positionTab: 'purpose' }
            }).catch(() => {});
            return;
        }
        
        showAddPositionModal.value = false;
    } catch (error) {
        console.error('Error creating position:', error);
    } finally {
        isLoading.value = false;
        proxy.$loader.setAppLoading(false);
    }
};

const handleAddRole = async (name) => {
    try {
        isLoading.value = true;
        roleErrorMessage.value = "";
        proxy.$loader.setAppLoading(true);
        const accountID = localStorage.getItem('currentAccountID');
        
        let { data } = await addRole(accountID, { name });
        
        if (data?.id) {
            showAddRoleModal.value = false;
            proxy.$loader.setAppLoading(false);
            await proxy.$nextTick();
            
            // Navigate to role detail page using the same route as OrganizationRolesTab
            proxy.$router.push({
                name: 'RoleDetailChild',
                params: { roleId: data.id },
                query: { roleTab: 'purpose' }
            }).catch(() => {});
            return;
        }
        
        showAddRoleModal.value = false;
    } catch (error) {
        console.error('Error creating role:', error);
        let errorMessage = error.response?.data?.[0] || error.response?.data?.name?.[0];
        if (errorMessage === "Same name already exists" || errorMessage?.includes("already exists")) {
            roleErrorMessage.value = "Name already exists. Only unique names are allowed.";
        } else {
            roleErrorMessage.value = errorMessage || "Failed to create role.";
        }
    }
    finally {
        isLoading.value = false;
        proxy.$loader.setAppLoading(false);
    }
};

const clearResponsibilityError = () => {
    responsibilityErrorMessage.value = '';
};

const clearRoleError = () => {
    roleErrorMessage.value = '';
};

const handleAddResponsibility = async (name) => {
    try {
        isLoading.value = true;
        proxy.$loader.setAppLoading(true);
        const accountID = localStorage.getItem('currentAccountID');
        
        // Call API directly to properly catch errors
        const payload = {
            name: name,
            account: accountID,
        };
        const { data: result } = await createResponsibility(accountID, payload);
        
        if (result?.id) {
            showAddResponsibilityModal.value = false;
            proxy.$loader.setAppLoading(false);
            await proxy.$nextTick();
            
            // Navigate to responsibility detail page using the same route pattern
            proxy.$router.push({
                name: 'ResponsibilityDetailChild',
                params: { resId: result.id },
                query: { resTab: 'purpose' }
            }).catch(() => {});
            return;
        }
        
        showAddResponsibilityModal.value = false;
    } catch (error) {
        console.error('Error creating responsibility:', error);
        // Keep dialog open and show error message in the input component
        let errorMessage = error.response?.data?.[0] || error.response?.data?.name?.[0];
        if (errorMessage === "Same name already exists" || errorMessage?.includes("already exists")) {
            responsibilityErrorMessage.value = "Name already exists. Only unique names are allowed.";
        } else {
            responsibilityErrorMessage.value = errorMessage || "Failed to create responsibility.";
        }
        // Dialog stays open (we don't set showAddResponsibilityModal.value = false here)
    } finally {
        isLoading.value = false;
        proxy.$loader.setAppLoading(false);
    }
};

const handleAddEmployee = async (payload) => {
    try {
        isLoading.value = true;
        proxy.$loader.setAppLoading(true);
        const accountID = localStorage.getItem('currentAccountID');
        
        const response = await createEmployee(accountID, payload);
        
        // Check if this is a new employee (no id in payload)
        if (!payload?.id && response?.data?.id) {
            showAddEmployeeModal.value = false;
            proxy.$loader.setAppLoading(false);
            await proxy.$nextTick();
            
            // Navigate to employee detail page using query parameters to show in drilldown mode
            // Use replace to ensure route updates and watcher fires
            proxy.$router.replace({
                name: 'NewOrganization',
                params: { tab: 'employees' },
                query: {
                  employeeId: String(response.data.id),
                  employeeTab: 'pathtoreadiness'
                }
            }).catch(() => {});
            return;
        }
        
        showAddEmployeeModal.value = false;
    } catch (error) {
        console.error('Error creating employee:', error);
    } finally {
        isLoading.value = false;
        proxy.$loader.setAppLoading(false);
    }
};

onMounted(async () => {
    if (!props.guideData) {
        try {
            proxy.$loader.show();
            await initializeKnock();
            proxy.$loader.hide();
        } catch (error) {
            proxy.$loader.hide();
            console.error('Error initializing Knock guide:', error);
        }
    }
});
</script>

<style scoped>
.organization-dashboard-tab { 
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.guide-video-banner {
    margin-top: 0px;
}
</style>